import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Send, Brain, User, Loader2, BookOpen } from 'lucide-react';
import { api } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';

export default function AskTheBrainPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchHistory(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchHistory = async () => {
    try { const { data } = await api.get(`/api/chat/history`); setMessages(data.reverse().map(m => ({ id: m.id, question: m.question, answer: m.answer, sources: m.sources || [] }))); }
    catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); if (!question.trim() || loading) return;
    const q = question.trim(); setQuestion(''); const tid = Date.now();
    setMessages(prev => [...prev, { id: tid, question: q, answer: null, sources: [] }]); setLoading(true);
    try { const { data } = await api.post(`/api/chat`, { question: q }); setMessages(prev => prev.map(m => m.id === tid ? { ...m, answer: data.answer, sources: data.sources || [] } : m)); }
    catch { setMessages(prev => prev.map(m => m.id === tid ? { ...m, answer: 'Sorry, an error occurred.' } : m)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-8rem)]" data-testid="ask-brain-page">
      <div className="mb-6 shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#1F2937]"><Brain size={24} className="text-[#3B82F6]" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#AAFF00] tracking-tight">Ask the Brain</h1>
            <p className="text-sm text-[#9CA3AF]">Get answers from your company's SOPs</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-6 pb-4 min-h-0">
        {messages.length === 0 && !loading && (
          <div className="text-center py-16">
            <Brain size={48} className="mx-auto text-[#374151] mb-4" />
            <p className="text-[#9CA3AF] text-lg">Ask a question about your company's processes</p>
            <p className="text-sm text-[#9CA3AF]/70 mt-1">The Brain searches your SOPs to find answers.</p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className="space-y-4">
            <div className="flex gap-3 justify-end">
              <div className="max-w-[80%] bg-[#AAFF00] text-[#080812] px-4 py-3 rounded-lg rounded-br-sm"><p className="text-sm font-medium">{msg.question}</p></div>
              <div className="w-8 h-8 rounded-full bg-[#AAFF00] flex items-center justify-center shrink-0"><User size={14} className="text-[#080812]" /></div>
            </div>
            {msg.answer ? (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1F2937] flex items-center justify-center shrink-0"><Brain size={14} className="text-[#3B82F6]" /></div>
                <div className="max-w-[80%]">
                  <div className="bg-[#1F2937] border border-[#374151] px-4 py-3 rounded-lg rounded-bl-sm">
                    <div className="prose prose-invert prose-sm max-w-none text-[#F9FAFB] text-sm"><ReactMarkdown>{msg.answer}</ReactMarkdown></div>
                  </div>
                  {msg.sources?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {msg.sources.map(s => <button key={s.id} onClick={() => navigate(`/library/${s.id}`)} className="flex items-center gap-1 px-2 py-1 bg-[#1F2937] rounded text-xs text-[#3B82F6] hover:text-[#AAFF00] border border-[#374151]" data-testid={`source-${s.id}`}><BookOpen size={10} /> {s.title}</button>)}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1F2937] flex items-center justify-center shrink-0"><Brain size={14} className="text-[#3B82F6]" /></div>
                <div className="bg-[#111827] border border-[#374151] px-4 py-3 rounded-lg"><Loader2 size={16} className="animate-spin text-[#3B82F6]" /></div>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="shrink-0 pt-4 border-t border-[#374151]">
        <div className="flex gap-2">
          <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about your company's procedures..." disabled={loading} className="flex-1 bg-[#1F2937] border-[#374151] text-[#F9FAFB] placeholder:text-[#9CA3AF]" data-testid="brain-question-input" />
          <Button type="submit" disabled={loading || !question.trim()} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] px-4" data-testid="brain-send-btn">{loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}</Button>
        </div>
      </form>
    </div>
  );
}
